// ⚠️ HARDCODED / TEMPORARY — ported verbatim from dane's etf_website
// (`frontend/app/etf-website/data/lev2x-sector-breakdown.ts`) on 2026-06-24.
// De-hardcode later: these per-underlying NAICS sector breakdowns should
// eventually come from the holdings feed / admin DB. Until then they are
// REQUIRED for the leveraged-2x Sector Breakdown (Underlying ETF) to render —
// the leveraged path has no live underlying-holdings feed, so without this
// lookup `FundSectors` receives empty `sectors` and renders nothing.
//
// Auto-generated from Sector_Breakdown_NAICS_US.xlsx
// (See Methodology & Coverage sheet: NAICS subsector = 3-digit code, weighted by
// actual fund constituents; sourced from issuer holdings + SEC NPORT-P + SSGA.)
//
// Per-underlying NAICS (US, 3-digit) sector breakdown by constituent weight.
// Sector-subtotal rows have `code: null` and `subsector: "(sector subtotal)"`.
// Subsector rows have a 3-digit NAICS `code` and a NAICS subsector name.
// Unclassified rows (Non-US, US-not-classified, Cash & long-tail) have
// `sector: ""` and `code: null` — consumers may surface them as "Other".
// Physical-commodity / futures trusts (IAU, PLTM, SIVR, COP-U CN, PDBC, USO,
// UNG, U-U CN, PALL) hold no equities, so `entries: []` — consumers should
// render the empty state for those underlyings.

import { getSectorColor } from "./compute-sectors";
import type { Sector } from "./fund-detail-types";

export interface NAICSSubsectorEntry {
  sector: string; // e.g. "Manufacturing"; "" for unclassified rows
  code: string | null; // 3-digit NAICS code; null for subtotals + unclassified
  subsector: string; // e.g. "Computer and Electronic Product Manufacturing"
  weightPct: number; // 0-100 percent
}

export interface SectorBreakdownPayload {
  underlyingTicker: string; // e.g. "XLRE"
  asOf: string; // YYYY-MM-DD; "" for N/A commodity sheets
  entries: NAICSSubsectorEntry[]; // [] for N/A commodity sheets
}

export const SECTOR_BREAKDOWN_BY_UNDERLYING: Readonly<Record<string, SectorBreakdownPayload>> =
  Object.freeze({
    VT: {
      underlyingTicker: "VT",
      asOf: "2026-04-30",
      entries: [
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0524,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "111",
          subsector: "Crop Production",
          weightPct: 0.0497,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "112",
          subsector: "Animal Production and Aquaculture",
          weightPct: 0.0027,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.7358,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "211",
          subsector: "Oil and Gas Extraction",
          weightPct: 0.3608,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "212",
          subsector: "Mining (except Oil and Gas)",
          weightPct: 0.3274,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "213",
          subsector: "Support Activities for Mining",
          weightPct: 0.0476,
        },
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 1.5358 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 1.5358 },
        { sector: "Construction", code: null, subsector: "(sector subtotal)", weightPct: 0.4519 },
        {
          sector: "Construction",
          code: "236",
          subsector: "Construction of Buildings",
          weightPct: 0.1496,
        },
        {
          sector: "Construction",
          code: "237",
          subsector: "Heavy and Civil Engineering Construction",
          weightPct: 0.0937,
        },
        {
          sector: "Construction",
          code: "238",
          subsector: "Specialty Trade Contractors",
          weightPct: 0.2086,
        },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 32.4516 },
        {
          sector: "Manufacturing",
          code: "311",
          subsector: "Food Manufacturing",
          weightPct: 0.7959,
        },
        {
          sector: "Manufacturing",
          code: "312",
          subsector: "Beverage and Tobacco Product Manufacturing",
          weightPct: 0.4071,
        },
        { sector: "Manufacturing", code: "313", subsector: "Textile Mills", weightPct: 0.0013 },
        {
          sector: "Manufacturing",
          code: "314",
          subsector: "Textile Product Mills",
          weightPct: 0.0045,
        },
        {
          sector: "Manufacturing",
          code: "315",
          subsector: "Apparel Manufacturing",
          weightPct: 0.0955,
        },
        {
          sector: "Manufacturing",
          code: "316",
          subsector: "Leather and Allied Product Manufacturing",
          weightPct: 0.094,
        },
        {
          sector: "Manufacturing",
          code: "321",
          subsector: "Wood Product Manufacturing",
          weightPct: 0.0229,
        },
        {
          sector: "Manufacturing",
          code: "322",
          subsector: "Paper Manufacturing",
          weightPct: 0.0859,
        },
        {
          sector: "Manufacturing",
          code: "324",
          subsector: "Petroleum and Coal Products Manufacturing",
          weightPct: 1.2664,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 3.9101,
        },
        {
          sector: "Manufacturing",
          code: "326",
          subsector: "Plastics and Rubber Products Manufacturing",
          weightPct: 0.0654,
        },
        {
          sector: "Manufacturing",
          code: "327",
          subsector: "Nonmetallic Mineral Product Manufacturing",
          weightPct: 0.0166,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 0.369,
        },
        {
          sector: "Manufacturing",
          code: "332",
          subsector: "Fabricated Metal Product Manufacturing",
          weightPct: 0.4375,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 1.5764,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 19.7302,
        },
        {
          sector: "Manufacturing",
          code: "335",
          subsector: "Electrical Equipment, Appliance, and Component Manufacturing",
          weightPct: 0.7739,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 2.0953,
        },
        {
          sector: "Manufacturing",
          code: "337",
          subsector: "Furniture and Related Product Manufacturing",
          weightPct: 0.023,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 0.6807,
        },
        { sector: "Wholesale Trade", code: null, subsector: "(sector subtotal)", weightPct: 0.326 },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 0.326,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 4.9332 },
        {
          sector: "Retail Trade",
          code: "441",
          subsector: "Motor Vehicle and Parts Dealers",
          weightPct: 0.2904,
        },
        {
          sector: "Retail Trade",
          code: "442",
          subsector: "Furniture & Home Furnishings Stores",
          weightPct: 0.0198,
        },
        {
          sector: "Retail Trade",
          code: "443",
          subsector: "Electronics & Appliance Stores",
          weightPct: 0.0091,
        },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 0.5577,
        },
        {
          sector: "Retail Trade",
          code: "445",
          subsector: "Food and Beverage Retailers",
          weightPct: 0.0494,
        },
        {
          sector: "Retail Trade",
          code: "446",
          subsector: "Health & Personal Care Stores",
          weightPct: 0.2769,
        },
        {
          sector: "Retail Trade",
          code: "448",
          subsector: "Clothing & Clothing Accessories Stores",
          weightPct: 0.2466,
        },
        {
          sector: "Retail Trade",
          code: "451",
          subsector: "Sporting Goods, Hobby, Book & Music Stores",
          weightPct: 0.026,
        },
        {
          sector: "Retail Trade",
          code: "452",
          subsector: "General Merchandise Stores",
          weightPct: 1.0727,
        },
        {
          sector: "Retail Trade",
          code: "453",
          subsector: "Miscellaneous Store Retailers",
          weightPct: 0.0332,
        },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 2.3514 },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.017,
        },
        {
          sector: "Transportation & Warehousing",
          code: "481",
          subsector: "Air Transportation",
          weightPct: 0.0259,
        },
        {
          sector: "Transportation & Warehousing",
          code: "482",
          subsector: "Rail Transportation",
          weightPct: 0.2844,
        },
        {
          sector: "Transportation & Warehousing",
          code: "483",
          subsector: "Water Transportation",
          weightPct: 0.0119,
        },
        {
          sector: "Transportation & Warehousing",
          code: "484",
          subsector: "Truck Transportation",
          weightPct: 0.1583,
        },
        {
          sector: "Transportation & Warehousing",
          code: "486",
          subsector: "Pipeline Transportation",
          weightPct: 0.2261,
        },
        {
          sector: "Transportation & Warehousing",
          code: "488",
          subsector: "Support Activities for Transportation",
          weightPct: 0.2329,
        },
        {
          sector: "Transportation & Warehousing",
          code: "492",
          subsector: "Couriers and Messengers",
          weightPct: 0.0775,
        },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 0.9444 },
        {
          sector: "Information",
          code: "511",
          subsector: "Publishing Industries (except Internet)",
          weightPct: 0.0269,
        },
        {
          sector: "Information",
          code: "512",
          subsector: "Motion Picture and Sound Recording Industries",
          weightPct: 0.0027,
        },
        {
          sector: "Information",
          code: "515",
          subsector: "Broadcasting (except Internet)",
          weightPct: 0.2353,
        },
        { sector: "Information", code: "517", subsector: "Telecommunications", weightPct: 0.4924 },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 0.1872,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 7.8683,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 2.8485,
        },
        {
          sector: "Finance & Insurance",
          code: "523",
          subsector:
            "Securities, Commodity Contracts, and Other Financial Investments and Related Activities",
          weightPct: 1.5581,
        },
        {
          sector: "Finance & Insurance",
          code: "524",
          subsector: "Insurance Carriers and Related Activities",
          weightPct: 2.119,
        },
        {
          sector: "Finance & Insurance",
          code: "525",
          subsector: "Funds, Trusts, and Other Financial Vehicles",
          weightPct: 1.3427,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.5738,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "531",
          subsector: "Real Estate",
          weightPct: 0.0951,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 0.469,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "533",
          subsector: "Lessors of Nonfinancial Intangible Assets (except Copyrighted Works)",
          weightPct: 0.0097,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 5.3548,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 5.3548,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.9809,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 1.8485,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "562",
          subsector: "Waste Management and Remediation Services",
          weightPct: 0.1324,
        },
        {
          sector: "Educational Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0221,
        },
        {
          sector: "Educational Services",
          code: "611",
          subsector: "Educational Services",
          weightPct: 0.0221,
        },
        {
          sector: "Health Care & Social Assistance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.2253,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "621",
          subsector: "Ambulatory Health Care Services",
          weightPct: 0.1151,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "622",
          subsector: "Hospitals",
          weightPct: 0.096,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "623",
          subsector: "Nursing and Residential Care Facilities",
          weightPct: 0.0106,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "624",
          subsector: "Social Assistance",
          weightPct: 0.0037,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.249,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "711",
          subsector: "Performing Arts, Spectator Sports, and Related Industries",
          weightPct: 0.0537,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "713",
          subsector: "Amusement, Gambling, and Recreation Industries",
          weightPct: 0.1953,
        },
        {
          sector: "Accommodation & Food Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.6565,
        },
        {
          sector: "Accommodation & Food Services",
          code: "721",
          subsector: "Accommodation",
          weightPct: 0.2064,
        },
        {
          sector: "Accommodation & Food Services",
          code: "722",
          subsector: "Food Services and Drinking Places",
          weightPct: 0.4501,
        },
        { sector: "Other Services", code: null, subsector: "(sector subtotal)", weightPct: 0.0179 },
        {
          sector: "Other Services",
          code: "811",
          subsector: "Repair and Maintenance",
          weightPct: 0.0006,
        },
        {
          sector: "Other Services",
          code: "812",
          subsector: "Personal and Laundry Services",
          weightPct: 0.0173,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 39.9965 },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 0.1067 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.5002 },
      ],
    },
    EWZ: {
      underlyingTicker: "EWZ",
      asOf: "2026-02-28",
      entries: [
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 99.7003 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.2997 },
      ],
    },
    FXI: {
      underlyingTicker: "FXI",
      asOf: "2026-01-31",
      entries: [
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 99.6883 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.3117 },
      ],
    },
    KWEB: {
      underlyingTicker: "KWEB",
      asOf: "2026-06-09",
      entries: [
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 3.7929 },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 3.7929,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 2.4676 },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 2.4676 },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 4.5923 },
        {
          sector: "Information",
          code: "515",
          subsector: "Broadcasting (except Internet)",
          weightPct: 2.5894,
        },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 2.0029,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.15,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 1.15,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 3.1498,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 3.1498,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 7.5619,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 7.5619,
        },
        {
          sector: "Educational Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 2.9348,
        },
        {
          sector: "Educational Services",
          code: "611",
          subsector: "Educational Services",
          weightPct: 2.9348,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 73.6129 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.7378 },
      ],
    },
    VWO: {
      underlyingTicker: "VWO",
      asOf: "2026-04-30",
      entries: [
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0309,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "211",
          subsector: "Oil and Gas Extraction",
          weightPct: 0.0171,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "212",
          subsector: "Mining (except Oil and Gas)",
          weightPct: 0.0138,
        },
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 0.0486 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 0.0486 },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 0.1751 },
        {
          sector: "Manufacturing",
          code: "312",
          subsector: "Beverage and Tobacco Product Manufacturing",
          weightPct: 0.0127,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 0.0168,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 0.0018,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 0.1306,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 0.0132,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 0.0306 },
        {
          sector: "Retail Trade",
          code: "445",
          subsector: "Food and Beverage Retailers",
          weightPct: 0.0018,
        },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 0.0288 },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0063,
        },
        {
          sector: "Transportation & Warehousing",
          code: "481",
          subsector: "Air Transportation",
          weightPct: 0.0063,
        },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 0.0981 },
        {
          sector: "Information",
          code: "515",
          subsector: "Broadcasting (except Internet)",
          weightPct: 0.0398,
        },
        { sector: "Information", code: "517", subsector: "Telecommunications", weightPct: 0.033 },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 0.0253,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.1656,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 0.1656,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0051,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 0.0051,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0849,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 0.0849,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.6628,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 0.6628,
        },
        {
          sector: "Educational Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.044,
        },
        {
          sector: "Educational Services",
          code: "611",
          subsector: "Educational Services",
          weightPct: 0.044,
        },
        {
          sector: "Accommodation & Food Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0225,
        },
        {
          sector: "Accommodation & Food Services",
          code: "721",
          subsector: "Accommodation",
          weightPct: 0.0225,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 97.9411 },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 0.1793 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.5051 },
      ],
    },
    VGK: {
      underlyingTicker: "VGK",
      asOf: "2026-04-30",
      entries: [
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.2015,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 0.2015,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 99.3018 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4967 },
      ],
    },
    VXUS: {
      underlyingTicker: "VXUS",
      asOf: "2026-04-30",
      entries: [
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 0.0094 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 0.0094 },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 0.0375 },
        {
          sector: "Manufacturing",
          code: "312",
          subsector: "Beverage and Tobacco Product Manufacturing",
          weightPct: 0.0034,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 0.0019,
        },
        {
          sector: "Manufacturing",
          code: "327",
          subsector: "Nonmetallic Mineral Product Manufacturing",
          weightPct: 0.0036,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 0.0238,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 0.0048,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 0.0086 },
        {
          sector: "Retail Trade",
          code: "445",
          subsector: "Food and Beverage Retailers",
          weightPct: 0.0007,
        },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 0.0079 },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0053,
        },
        {
          sector: "Transportation & Warehousing",
          code: "481",
          subsector: "Air Transportation",
          weightPct: 0.0031,
        },
        {
          sector: "Transportation & Warehousing",
          code: "488",
          subsector: "Support Activities for Transportation",
          weightPct: 0.0022,
        },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 0.0225 },
        {
          sector: "Information",
          code: "515",
          subsector: "Broadcasting (except Internet)",
          weightPct: 0.0107,
        },
        { sector: "Information", code: "517", subsector: "Telecommunications", weightPct: 0.0048 },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 0.0071,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0197,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 0.0197,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0746,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 0.0746,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0246,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 0.0246,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.1787,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 0.1787,
        },
        {
          sector: "Educational Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0119,
        },
        {
          sector: "Educational Services",
          code: "611",
          subsector: "Educational Services",
          weightPct: 0.0119,
        },
        {
          sector: "Accommodation & Food Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0062,
        },
        {
          sector: "Accommodation & Food Services",
          code: "721",
          subsector: "Accommodation",
          weightPct: 0.0062,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 98.9259 },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 0.1739 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.5012 },
      ],
    },
    IAU: { underlyingTicker: "IAU", asOf: "", entries: [] },
    INDA: {
      underlyingTicker: "INDA",
      asOf: "2026-02-28",
      entries: [
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 99.5435 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4565 },
      ],
    },
    VOO: {
      underlyingTicker: "VOO",
      asOf: "2026-04-30",
      entries: [
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0884,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "111",
          subsector: "Crop Production",
          weightPct: 0.0884,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.949,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "211",
          subsector: "Oil and Gas Extraction",
          weightPct: 0.4347,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "212",
          subsector: "Mining (except Oil and Gas)",
          weightPct: 0.4568,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "213",
          subsector: "Support Activities for Mining",
          weightPct: 0.0575,
        },
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 2.4299 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 2.4299 },
        { sector: "Construction", code: null, subsector: "(sector subtotal)", weightPct: 0.5362 },
        {
          sector: "Construction",
          code: "236",
          subsector: "Construction of Buildings",
          weightPct: 0.1654,
        },
        {
          sector: "Construction",
          code: "237",
          subsector: "Heavy and Civil Engineering Construction",
          weightPct: 0.0246,
        },
        {
          sector: "Construction",
          code: "238",
          subsector: "Specialty Trade Contractors",
          weightPct: 0.3461,
        },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 54.3076 },
        {
          sector: "Manufacturing",
          code: "311",
          subsector: "Food Manufacturing",
          weightPct: 1.2896,
        },
        {
          sector: "Manufacturing",
          code: "312",
          subsector: "Beverage and Tobacco Product Manufacturing",
          weightPct: 0.7041,
        },
        {
          sector: "Manufacturing",
          code: "315",
          subsector: "Apparel Manufacturing",
          weightPct: 0.1427,
        },
        {
          sector: "Manufacturing",
          code: "316",
          subsector: "Leather and Allied Product Manufacturing",
          weightPct: 0.157,
        },
        {
          sector: "Manufacturing",
          code: "322",
          subsector: "Paper Manufacturing",
          weightPct: 0.1306,
        },
        {
          sector: "Manufacturing",
          code: "324",
          subsector: "Petroleum and Coal Products Manufacturing",
          weightPct: 2.2456,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 6.1968,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 0.5008,
        },
        {
          sector: "Manufacturing",
          code: "332",
          subsector: "Fabricated Metal Product Manufacturing",
          weightPct: 0.5812,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 2.4361,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 34.2765,
        },
        {
          sector: "Manufacturing",
          code: "335",
          subsector: "Electrical Equipment, Appliance, and Component Manufacturing",
          weightPct: 1.1692,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 3.4183,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 1.059,
        },
        {
          sector: "Wholesale Trade",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.1647,
        },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 0.1647,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 8.532 },
        {
          sector: "Retail Trade",
          code: "441",
          subsector: "Motor Vehicle and Parts Dealers",
          weightPct: 0.4423,
        },
        {
          sector: "Retail Trade",
          code: "442",
          subsector: "Furniture & Home Furnishings Stores",
          weightPct: 0.0351,
        },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 0.9827,
        },
        {
          sector: "Retail Trade",
          code: "445",
          subsector: "Food and Beverage Retailers",
          weightPct: 0.06,
        },
        {
          sector: "Retail Trade",
          code: "446",
          subsector: "Health & Personal Care Stores",
          weightPct: 0.5049,
        },
        {
          sector: "Retail Trade",
          code: "448",
          subsector: "Clothing & Clothing Accessories Stores",
          weightPct: 0.402,
        },
        {
          sector: "Retail Trade",
          code: "452",
          subsector: "General Merchandise Stores",
          weightPct: 1.8352,
        },
        {
          sector: "Retail Trade",
          code: "453",
          subsector: "Miscellaneous Store Retailers",
          weightPct: 0.0387,
        },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 4.231 },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.7076,
        },
        {
          sector: "Transportation & Warehousing",
          code: "481",
          subsector: "Air Transportation",
          weightPct: 0.1491,
        },
        {
          sector: "Transportation & Warehousing",
          code: "482",
          subsector: "Rail Transportation",
          weightPct: 0.5117,
        },
        {
          sector: "Transportation & Warehousing",
          code: "484",
          subsector: "Truck Transportation",
          weightPct: 0.2244,
        },
        {
          sector: "Transportation & Warehousing",
          code: "486",
          subsector: "Pipeline Transportation",
          weightPct: 0.3462,
        },
        {
          sector: "Transportation & Warehousing",
          code: "488",
          subsector: "Support Activities for Transportation",
          weightPct: 0.3345,
        },
        {
          sector: "Transportation & Warehousing",
          code: "492",
          subsector: "Couriers and Messengers",
          weightPct: 0.1416,
        },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 1.3351 },
        {
          sector: "Information",
          code: "515",
          subsector: "Broadcasting (except Internet)",
          weightPct: 0.3098,
        },
        { sector: "Information", code: "517", subsector: "Telecommunications", weightPct: 0.8027 },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 0.2225,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 11.8415,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 4.149,
        },
        {
          sector: "Finance & Insurance",
          code: "523",
          subsector:
            "Securities, Commodity Contracts, and Other Financial Investments and Related Activities",
          weightPct: 2.4772,
        },
        {
          sector: "Finance & Insurance",
          code: "524",
          subsector: "Insurance Carriers and Related Activities",
          weightPct: 3.4493,
        },
        {
          sector: "Finance & Insurance",
          code: "525",
          subsector: "Funds, Trusts, and Other Financial Vehicles",
          weightPct: 1.766,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.8331,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "531",
          subsector: "Real Estate",
          weightPct: 0.0936,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 0.7395,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 9.2047,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 9.2047,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 3.0794,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 2.8711,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "562",
          subsector: "Waste Management and Remediation Services",
          weightPct: 0.2083,
        },
        {
          sector: "Health Care & Social Assistance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.1795,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "621",
          subsector: "Ambulatory Health Care Services",
          weightPct: 0.0692,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "622",
          subsector: "Hospitals",
          weightPct: 0.1103,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.3587,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "711",
          subsector: "Performing Arts, Spectator Sports, and Related Industries",
          weightPct: 0.0605,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "713",
          subsector: "Amusement, Gambling, and Recreation Industries",
          weightPct: 0.2982,
        },
        {
          sector: "Accommodation & Food Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.9917,
        },
        {
          sector: "Accommodation & Food Services",
          code: "721",
          subsector: "Accommodation",
          weightPct: 0.2764,
        },
        {
          sector: "Accommodation & Food Services",
          code: "722",
          subsector: "Food Services and Drinking Places",
          weightPct: 0.7153,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 2.9255 },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 0.0442 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4911 },
      ],
    },
    MGK: {
      underlyingTicker: "MGK",
      asOf: "2026-04-30",
      entries: [
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 65.8403 },
        {
          sector: "Manufacturing",
          code: "312",
          subsector: "Beverage and Tobacco Product Manufacturing",
          weightPct: 0.3741,
        },
        {
          sector: "Manufacturing",
          code: "315",
          subsector: "Apparel Manufacturing",
          weightPct: 0.266,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 3.7706,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 0.4712,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 2.3422,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 51.6535,
        },
        {
          sector: "Manufacturing",
          code: "335",
          subsector: "Electrical Equipment, Appliance, and Component Manufacturing",
          weightPct: 1.24,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 4.5574,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 1.1652,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 6.9975 },
        {
          sector: "Retail Trade",
          code: "441",
          subsector: "Motor Vehicle and Parts Dealers",
          weightPct: 0.7836,
        },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 0.316,
        },
        {
          sector: "Retail Trade",
          code: "448",
          subsector: "Clothing & Clothing Accessories Stores",
          weightPct: 0.7714,
        },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 5.1265 },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.6243,
        },
        {
          sector: "Transportation & Warehousing",
          code: "488",
          subsector: "Support Activities for Transportation",
          weightPct: 0.6243,
        },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 0.3153 },
        { sector: "Information", code: "517", subsector: "Telecommunications", weightPct: 0.3153 },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.5802,
        },
        {
          sector: "Finance & Insurance",
          code: "523",
          subsector:
            "Securities, Commodity Contracts, and Other Financial Investments and Related Activities",
          weightPct: 0.2885,
        },
        {
          sector: "Finance & Insurance",
          code: "525",
          subsector: "Funds, Trusts, and Other Financial Vehicles",
          weightPct: 1.2917,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.5006,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 1.5006,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 16.0192,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 16.0192,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 5.3114,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 5.3114,
        },
        {
          sector: "Accommodation & Food Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.3296,
        },
        {
          sector: "Accommodation & Food Services",
          code: "721",
          subsector: "Accommodation",
          weightPct: 0.808,
        },
        {
          sector: "Accommodation & Food Services",
          code: "722",
          subsector: "Food Services and Drinking Places",
          weightPct: 0.5216,
        },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4817 },
      ],
    },
    VO: {
      underlyingTicker: "VO",
      asOf: "2026-04-30",
      entries: [
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.5619,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "111",
          subsector: "Crop Production",
          weightPct: 0.5619,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 3.0979,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "211",
          subsector: "Oil and Gas Extraction",
          weightPct: 1.3813,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "212",
          subsector: "Mining (except Oil and Gas)",
          weightPct: 1.3929,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "213",
          subsector: "Support Activities for Mining",
          weightPct: 0.3238,
        },
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 9.5262 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 9.5262 },
        { sector: "Construction", code: null, subsector: "(sector subtotal)", weightPct: 2.439 },
        {
          sector: "Construction",
          code: "236",
          subsector: "Construction of Buildings",
          weightPct: 1.0047,
        },
        {
          sector: "Construction",
          code: "238",
          subsector: "Specialty Trade Contractors",
          weightPct: 1.4343,
        },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 38.3487 },
        {
          sector: "Manufacturing",
          code: "311",
          subsector: "Food Manufacturing",
          weightPct: 1.9457,
        },
        {
          sector: "Manufacturing",
          code: "315",
          subsector: "Apparel Manufacturing",
          weightPct: 0.4504,
        },
        {
          sector: "Manufacturing",
          code: "322",
          subsector: "Paper Manufacturing",
          weightPct: 0.6884,
        },
        {
          sector: "Manufacturing",
          code: "324",
          subsector: "Petroleum and Coal Products Manufacturing",
          weightPct: 2.2391,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 3.0767,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 2.2895,
        },
        {
          sector: "Manufacturing",
          code: "332",
          subsector: "Fabricated Metal Product Manufacturing",
          weightPct: 0.5137,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 3.8733,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 17.5774,
        },
        {
          sector: "Manufacturing",
          code: "335",
          subsector: "Electrical Equipment, Appliance, and Component Manufacturing",
          weightPct: 0.6909,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 2.9982,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 2.0055,
        },
        {
          sector: "Wholesale Trade",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.5006,
        },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 1.5006,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 5.5179 },
        {
          sector: "Retail Trade",
          code: "441",
          subsector: "Motor Vehicle and Parts Dealers",
          weightPct: 1.1496,
        },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 0.7115,
        },
        {
          sector: "Retail Trade",
          code: "445",
          subsector: "Food and Beverage Retailers",
          weightPct: 0.3937,
        },
        {
          sector: "Retail Trade",
          code: "446",
          subsector: "Health & Personal Care Stores",
          weightPct: 1.0389,
        },
        {
          sector: "Retail Trade",
          code: "448",
          subsector: "Clothing & Clothing Accessories Stores",
          weightPct: 0.7481,
        },
        {
          sector: "Retail Trade",
          code: "452",
          subsector: "General Merchandise Stores",
          weightPct: 1.1442,
        },
        {
          sector: "Retail Trade",
          code: "453",
          subsector: "Miscellaneous Store Retailers",
          weightPct: 0.2421,
        },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 0.0897 },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 3.6298,
        },
        {
          sector: "Transportation & Warehousing",
          code: "481",
          subsector: "Air Transportation",
          weightPct: 0.9397,
        },
        {
          sector: "Transportation & Warehousing",
          code: "484",
          subsector: "Truck Transportation",
          weightPct: 0.4047,
        },
        {
          sector: "Transportation & Warehousing",
          code: "486",
          subsector: "Pipeline Transportation",
          weightPct: 1.3565,
        },
        {
          sector: "Transportation & Warehousing",
          code: "488",
          subsector: "Support Activities for Transportation",
          weightPct: 0.4956,
        },
        {
          sector: "Transportation & Warehousing",
          code: "492",
          subsector: "Couriers and Messengers",
          weightPct: 0.4333,
        },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 1.6231 },
        {
          sector: "Information",
          code: "515",
          subsector: "Broadcasting (except Internet)",
          weightPct: 0.9014,
        },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 0.7217,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 16.9182,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 3.9127,
        },
        {
          sector: "Finance & Insurance",
          code: "523",
          subsector:
            "Securities, Commodity Contracts, and Other Financial Investments and Related Activities",
          weightPct: 3.5446,
        },
        {
          sector: "Finance & Insurance",
          code: "524",
          subsector: "Insurance Carriers and Related Activities",
          weightPct: 4.6456,
        },
        {
          sector: "Finance & Insurance",
          code: "525",
          subsector: "Funds, Trusts, and Other Financial Vehicles",
          weightPct: 4.8154,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.4499,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "531",
          subsector: "Real Estate",
          weightPct: 0.5125,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 0.9374,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.542,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 1.542,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 3.6454,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 3.6454,
        },
        {
          sector: "Health Care & Social Assistance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.4315,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "621",
          subsector: "Ambulatory Health Care Services",
          weightPct: 0.4315,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.2636,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "711",
          subsector: "Performing Arts, Spectator Sports, and Related Industries",
          weightPct: 0.2636,
        },
        {
          sector: "Accommodation & Food Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.6772,
        },
        {
          sector: "Accommodation & Food Services",
          code: "721",
          subsector: "Accommodation",
          weightPct: 0.5449,
        },
        {
          sector: "Accommodation & Food Services",
          code: "722",
          subsector: "Food Services and Drinking Places",
          weightPct: 1.1322,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 7.0779 },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 0.2638 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4853 },
      ],
    },
    PLTM: { underlyingTicker: "PLTM", asOf: "", entries: [] },
    SIVR: { underlyingTicker: "SIVR", asOf: "", entries: [] },
    VB: {
      underlyingTicker: "VB",
      asOf: "2026-04-30",
      entries: [
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0523,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "112",
          subsector: "Animal Production and Aquaculture",
          weightPct: 0.0414,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "115",
          subsector: "Support Activities for Agriculture and Forestry",
          weightPct: 0.0109,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 3.4356,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "211",
          subsector: "Oil and Gas Extraction",
          weightPct: 2.2979,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "212",
          subsector: "Mining (except Oil and Gas)",
          weightPct: 0.9039,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "213",
          subsector: "Support Activities for Mining",
          weightPct: 0.2338,
        },
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 3.4033 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 3.4033 },
        { sector: "Construction", code: null, subsector: "(sector subtotal)", weightPct: 3.3556 },
        {
          sector: "Construction",
          code: "236",
          subsector: "Construction of Buildings",
          weightPct: 0.7171,
        },
        {
          sector: "Construction",
          code: "237",
          subsector: "Heavy and Civil Engineering Construction",
          weightPct: 1.3461,
        },
        {
          sector: "Construction",
          code: "238",
          subsector: "Specialty Trade Contractors",
          weightPct: 1.2925,
        },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 39.2635 },
        {
          sector: "Manufacturing",
          code: "311",
          subsector: "Food Manufacturing",
          weightPct: 1.1762,
        },
        {
          sector: "Manufacturing",
          code: "312",
          subsector: "Beverage and Tobacco Product Manufacturing",
          weightPct: 0.2452,
        },
        { sector: "Manufacturing", code: "313", subsector: "Textile Mills", weightPct: 0.0214 },
        {
          sector: "Manufacturing",
          code: "314",
          subsector: "Textile Product Mills",
          weightPct: 0.0817,
        },
        {
          sector: "Manufacturing",
          code: "315",
          subsector: "Apparel Manufacturing",
          weightPct: 0.4852,
        },
        {
          sector: "Manufacturing",
          code: "316",
          subsector: "Leather and Allied Product Manufacturing",
          weightPct: 0.6873,
        },
        {
          sector: "Manufacturing",
          code: "321",
          subsector: "Wood Product Manufacturing",
          weightPct: 0.3222,
        },
        {
          sector: "Manufacturing",
          code: "322",
          subsector: "Paper Manufacturing",
          weightPct: 0.2794,
        },
        {
          sector: "Manufacturing",
          code: "323",
          subsector: "Printing and Related Support Activities",
          weightPct: 0.0173,
        },
        {
          sector: "Manufacturing",
          code: "324",
          subsector: "Petroleum and Coal Products Manufacturing",
          weightPct: 0.1765,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 7.832,
        },
        {
          sector: "Manufacturing",
          code: "326",
          subsector: "Plastics and Rubber Products Manufacturing",
          weightPct: 0.9456,
        },
        {
          sector: "Manufacturing",
          code: "327",
          subsector: "Nonmetallic Mineral Product Manufacturing",
          weightPct: 0.2426,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 1.446,
        },
        {
          sector: "Manufacturing",
          code: "332",
          subsector: "Fabricated Metal Product Manufacturing",
          weightPct: 2.3936,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 4.2901,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 11.6131,
        },
        {
          sector: "Manufacturing",
          code: "335",
          subsector: "Electrical Equipment, Appliance, and Component Manufacturing",
          weightPct: 1.8636,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 2.904,
        },
        {
          sector: "Manufacturing",
          code: "337",
          subsector: "Furniture and Related Product Manufacturing",
          weightPct: 0.3054,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 1.9351,
        },
        {
          sector: "Wholesale Trade",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 2.8173,
        },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 2.8173,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 3.9214 },
        {
          sector: "Retail Trade",
          code: "441",
          subsector: "Motor Vehicle and Parts Dealers",
          weightPct: 1.234,
        },
        {
          sector: "Retail Trade",
          code: "442",
          subsector: "Furniture & Home Furnishings Stores",
          weightPct: 0.2813,
        },
        {
          sector: "Retail Trade",
          code: "443",
          subsector: "Electronics & Appliance Stores",
          weightPct: 0.1308,
        },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 0.176,
        },
        {
          sector: "Retail Trade",
          code: "445",
          subsector: "Food and Beverage Retailers",
          weightPct: 0.1919,
        },
        {
          sector: "Retail Trade",
          code: "448",
          subsector: "Clothing & Clothing Accessories Stores",
          weightPct: 0.3644,
        },
        {
          sector: "Retail Trade",
          code: "451",
          subsector: "Sporting Goods, Hobby, Book & Music Stores",
          weightPct: 0.3573,
        },
        {
          sector: "Retail Trade",
          code: "452",
          subsector: "General Merchandise Stores",
          weightPct: 0.6874,
        },
        {
          sector: "Retail Trade",
          code: "453",
          subsector: "Miscellaneous Store Retailers",
          weightPct: 0.1758,
        },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 0.3226 },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 2.6451,
        },
        {
          sector: "Transportation & Warehousing",
          code: "481",
          subsector: "Air Transportation",
          weightPct: 0.2354,
        },
        {
          sector: "Transportation & Warehousing",
          code: "483",
          subsector: "Water Transportation",
          weightPct: 0.2211,
        },
        {
          sector: "Transportation & Warehousing",
          code: "484",
          subsector: "Truck Transportation",
          weightPct: 0.7015,
        },
        {
          sector: "Transportation & Warehousing",
          code: "486",
          subsector: "Pipeline Transportation",
          weightPct: 0.5421,
        },
        {
          sector: "Transportation & Warehousing",
          code: "488",
          subsector: "Support Activities for Transportation",
          weightPct: 0.945,
        },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 2.8646 },
        {
          sector: "Information",
          code: "511",
          subsector: "Publishing Industries (except Internet)",
          weightPct: 0.352,
        },
        {
          sector: "Information",
          code: "512",
          subsector: "Motion Picture and Sound Recording Industries",
          weightPct: 0.0397,
        },
        {
          sector: "Information",
          code: "515",
          subsector: "Broadcasting (except Internet)",
          weightPct: 0.8509,
        },
        { sector: "Information", code: "517", subsector: "Telecommunications", weightPct: 0.9516 },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 0.6704,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 18.7206,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 6.1953,
        },
        {
          sector: "Finance & Insurance",
          code: "523",
          subsector:
            "Securities, Commodity Contracts, and Other Financial Investments and Related Activities",
          weightPct: 2.6155,
        },
        {
          sector: "Finance & Insurance",
          code: "524",
          subsector: "Insurance Carriers and Related Activities",
          weightPct: 3.2616,
        },
        {
          sector: "Finance & Insurance",
          code: "525",
          subsector: "Funds, Trusts, and Other Financial Vehicles",
          weightPct: 6.6482,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.3271,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "531",
          subsector: "Real Estate",
          weightPct: 0.7805,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 0.3968,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "533",
          subsector: "Lessors of Nonfinancial Intangible Assets (except Copyrighted Works)",
          weightPct: 0.1498,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 3.4534,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 3.4534,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.9994,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 1.7413,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "562",
          subsector: "Waste Management and Remediation Services",
          weightPct: 0.2581,
        },
        {
          sector: "Educational Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.276,
        },
        {
          sector: "Educational Services",
          code: "611",
          subsector: "Educational Services",
          weightPct: 0.276,
        },
        {
          sector: "Health Care & Social Assistance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.981,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "621",
          subsector: "Ambulatory Health Care Services",
          weightPct: 1.1727,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "622",
          subsector: "Hospitals",
          weightPct: 0.5023,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "623",
          subsector: "Nursing and Residential Care Facilities",
          weightPct: 0.2479,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "624",
          subsector: "Social Assistance",
          weightPct: 0.0581,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.8492,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "711",
          subsector: "Performing Arts, Spectator Sports, and Related Industries",
          weightPct: 0.3739,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "713",
          subsector: "Amusement, Gambling, and Recreation Industries",
          weightPct: 0.4753,
        },
        {
          sector: "Accommodation & Food Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.5328,
        },
        {
          sector: "Accommodation & Food Services",
          code: "721",
          subsector: "Accommodation",
          weightPct: 0.7521,
        },
        {
          sector: "Accommodation & Food Services",
          code: "722",
          subsector: "Food Services and Drinking Places",
          weightPct: 0.7807,
        },
        { sector: "Other Services", code: null, subsector: "(sector subtotal)", weightPct: 0.2711 },
        {
          sector: "Other Services",
          code: "811",
          subsector: "Repair and Maintenance",
          weightPct: 0.0102,
        },
        {
          sector: "Other Services",
          code: "812",
          subsector: "Personal and Laundry Services",
          weightPct: 0.2609,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 6.6616 },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 0.6694 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4997 },
      ],
    },
    EWY: {
      underlyingTicker: "EWY",
      asOf: "2026-02-28",
      entries: [
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 99.2882 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.7118 },
      ],
    },
    EWT: {
      underlyingTicker: "EWT",
      asOf: "2026-02-28",
      entries: [
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 99.7357 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.2643 },
      ],
    },
    VTI: {
      underlyingTicker: "VTI",
      asOf: "2026-04-30",
      entries: [
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0861,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "111",
          subsector: "Crop Production",
          weightPct: 0.0803,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "112",
          subsector: "Animal Production and Aquaculture",
          weightPct: 0.0046,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "115",
          subsector: "Support Activities for Agriculture and Forestry",
          weightPct: 0.0012,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.19,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "211",
          subsector: "Oil and Gas Extraction",
          weightPct: 0.5809,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "212",
          subsector: "Mining (except Oil and Gas)",
          weightPct: 0.5292,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "213",
          subsector: "Support Activities for Mining",
          weightPct: 0.0799,
        },
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 2.506 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 2.506 },
        { sector: "Construction", code: null, subsector: "(sector subtotal)", weightPct: 0.7576 },
        {
          sector: "Construction",
          code: "236",
          subsector: "Construction of Buildings",
          weightPct: 0.2384,
        },
        {
          sector: "Construction",
          code: "237",
          subsector: "Heavy and Civil Engineering Construction",
          weightPct: 0.1607,
        },
        {
          sector: "Construction",
          code: "238",
          subsector: "Specialty Trade Contractors",
          weightPct: 0.3585,
        },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 52.802 },
        {
          sector: "Manufacturing",
          code: "311",
          subsector: "Food Manufacturing",
          weightPct: 1.2367,
        },
        {
          sector: "Manufacturing",
          code: "312",
          subsector: "Beverage and Tobacco Product Manufacturing",
          weightPct: 0.6555,
        },
        { sector: "Manufacturing", code: "313", subsector: "Textile Mills", weightPct: 0.0024 },
        {
          sector: "Manufacturing",
          code: "314",
          subsector: "Textile Product Mills",
          weightPct: 0.0101,
        },
        {
          sector: "Manufacturing",
          code: "315",
          subsector: "Apparel Manufacturing",
          weightPct: 0.1628,
        },
        {
          sector: "Manufacturing",
          code: "316",
          subsector: "Leather and Allied Product Manufacturing",
          weightPct: 0.1534,
        },
        {
          sector: "Manufacturing",
          code: "321",
          subsector: "Wood Product Manufacturing",
          weightPct: 0.04,
        },
        {
          sector: "Manufacturing",
          code: "322",
          subsector: "Paper Manufacturing",
          weightPct: 0.1316,
        },
        {
          sector: "Manufacturing",
          code: "323",
          subsector: "Printing and Related Support Activities",
          weightPct: 0.0019,
        },
        {
          sector: "Manufacturing",
          code: "324",
          subsector: "Petroleum and Coal Products Manufacturing",
          weightPct: 2.0328,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 6.6086,
        },
        {
          sector: "Manufacturing",
          code: "326",
          subsector: "Plastics and Rubber Products Manufacturing",
          weightPct: 0.1083,
        },
        {
          sector: "Manufacturing",
          code: "327",
          subsector: "Nonmetallic Mineral Product Manufacturing",
          weightPct: 0.0271,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 0.5873,
        },
        {
          sector: "Manufacturing",
          code: "332",
          subsector: "Fabricated Metal Product Manufacturing",
          weightPct: 0.7307,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 2.5622,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 31.847,
        },
        {
          sector: "Manufacturing",
          code: "335",
          subsector: "Electrical Equipment, Appliance, and Component Manufacturing",
          weightPct: 1.2824,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 3.4289,
        },
        {
          sector: "Manufacturing",
          code: "337",
          subsector: "Furniture and Related Product Manufacturing",
          weightPct: 0.0369,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 1.1553,
        },
        {
          sector: "Wholesale Trade",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.5487,
        },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 0.5487,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 7.9462 },
        {
          sector: "Retail Trade",
          code: "441",
          subsector: "Motor Vehicle and Parts Dealers",
          weightPct: 0.469,
        },
        {
          sector: "Retail Trade",
          code: "442",
          subsector: "Furniture & Home Furnishings Stores",
          weightPct: 0.0315,
        },
        {
          sector: "Retail Trade",
          code: "443",
          subsector: "Electronics & Appliance Stores",
          weightPct: 0.0146,
        },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 0.9011,
        },
        {
          sector: "Retail Trade",
          code: "445",
          subsector: "Food and Beverage Retailers",
          weightPct: 0.0796,
        },
        {
          sector: "Retail Trade",
          code: "446",
          subsector: "Health & Personal Care Stores",
          weightPct: 0.4489,
        },
        {
          sector: "Retail Trade",
          code: "448",
          subsector: "Clothing & Clothing Accessories Stores",
          weightPct: 0.4003,
        },
        {
          sector: "Retail Trade",
          code: "451",
          subsector: "Sporting Goods, Hobby, Book & Music Stores",
          weightPct: 0.0399,
        },
        {
          sector: "Retail Trade",
          code: "452",
          subsector: "General Merchandise Stores",
          weightPct: 1.7339,
        },
        {
          sector: "Retail Trade",
          code: "453",
          subsector: "Miscellaneous Store Retailers",
          weightPct: 0.0604,
        },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 3.767 },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.7706,
        },
        {
          sector: "Transportation & Warehousing",
          code: "481",
          subsector: "Air Transportation",
          weightPct: 0.1623,
        },
        {
          sector: "Transportation & Warehousing",
          code: "482",
          subsector: "Rail Transportation",
          weightPct: 0.4577,
        },
        {
          sector: "Transportation & Warehousing",
          code: "483",
          subsector: "Water Transportation",
          weightPct: 0.0248,
        },
        {
          sector: "Transportation & Warehousing",
          code: "484",
          subsector: "Truck Transportation",
          weightPct: 0.2549,
        },
        {
          sector: "Transportation & Warehousing",
          code: "486",
          subsector: "Pipeline Transportation",
          weightPct: 0.3757,
        },
        {
          sector: "Transportation & Warehousing",
          code: "488",
          subsector: "Support Activities for Transportation",
          weightPct: 0.3713,
        },
        {
          sector: "Transportation & Warehousing",
          code: "492",
          subsector: "Couriers and Messengers",
          weightPct: 0.1239,
        },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 1.4896 },
        {
          sector: "Information",
          code: "511",
          subsector: "Publishing Industries (except Internet)",
          weightPct: 0.0407,
        },
        {
          sector: "Information",
          code: "512",
          subsector: "Motion Picture and Sound Recording Industries",
          weightPct: 0.0057,
        },
        {
          sector: "Information",
          code: "515",
          subsector: "Broadcasting (except Internet)",
          weightPct: 0.3751,
        },
        { sector: "Information", code: "517", subsector: "Telecommunications", weightPct: 0.7644 },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 0.3037,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 12.7587,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 4.5794,
        },
        {
          sector: "Finance & Insurance",
          code: "523",
          subsector:
            "Securities, Commodity Contracts, and Other Financial Investments and Related Activities",
          weightPct: 2.535,
        },
        {
          sector: "Finance & Insurance",
          code: "524",
          subsector: "Insurance Carriers and Related Activities",
          weightPct: 3.4538,
        },
        {
          sector: "Finance & Insurance",
          code: "525",
          subsector: "Funds, Trusts, and Other Financial Vehicles",
          weightPct: 2.1904,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.9333,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "531",
          subsector: "Real Estate",
          weightPct: 0.1626,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 0.754,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "533",
          subsector: "Lessors of Nonfinancial Intangible Assets (except Copyrighted Works)",
          weightPct: 0.0167,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 8.5629,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 8.5629,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 2.9313,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 2.7032,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "562",
          subsector: "Waste Management and Remediation Services",
          weightPct: 0.228,
        },
        {
          sector: "Educational Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0394,
        },
        {
          sector: "Educational Services",
          code: "611",
          subsector: "Educational Services",
          weightPct: 0.0394,
        },
        {
          sector: "Health Care & Social Assistance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.4022,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "621",
          subsector: "Ambulatory Health Care Services",
          weightPct: 0.2131,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "622",
          subsector: "Hospitals",
          weightPct: 0.1548,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "623",
          subsector: "Nursing and Residential Care Facilities",
          weightPct: 0.0277,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "624",
          subsector: "Social Assistance",
          weightPct: 0.0065,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.4127,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "711",
          subsector: "Performing Arts, Spectator Sports, and Related Industries",
          weightPct: 0.0914,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "713",
          subsector: "Amusement, Gambling, and Recreation Industries",
          weightPct: 0.3213,
        },
        {
          sector: "Accommodation & Food Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.0544,
        },
        {
          sector: "Accommodation & Food Services",
          code: "721",
          subsector: "Accommodation",
          weightPct: 0.3295,
        },
        {
          sector: "Accommodation & Food Services",
          code: "722",
          subsector: "Food Services and Drinking Places",
          weightPct: 0.7249,
        },
        { sector: "Other Services", code: null, subsector: "(sector subtotal)", weightPct: 0.0302 },
        {
          sector: "Other Services",
          code: "811",
          subsector: "Repair and Maintenance",
          weightPct: 0.0011,
        },
        {
          sector: "Other Services",
          code: "812",
          subsector: "Personal and Laundry Services",
          weightPct: 0.0291,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 3.1513 },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 0.128 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4989 },
      ],
    },
    XBI: {
      underlyingTicker: "XBI",
      asOf: "2026-06-08",
      entries: [
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 92.604 },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 92.484,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 0.12,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.3107,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 0.3107,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.6914,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 1.6914,
        },
        {
          sector: "Health Care & Social Assistance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 4.8731,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "621",
          subsector: "Ambulatory Health Care Services",
          weightPct: 4.8731,
        },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 0.0975 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4233 },
      ],
    },
    XLY: {
      underlyingTicker: "XLY",
      asOf: "2026-06-08",
      entries: [
        { sector: "Construction", code: null, subsector: "(sector subtotal)", weightPct: 2.3141 },
        {
          sector: "Construction",
          code: "236",
          subsector: "Construction of Buildings",
          weightPct: 2.3141,
        },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 27.283 },
        {
          sector: "Manufacturing",
          code: "315",
          subsector: "Apparel Manufacturing",
          weightPct: 0.634,
        },
        {
          sector: "Manufacturing",
          code: "316",
          subsector: "Leather and Allied Product Manufacturing",
          weightPct: 2.2491,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 0.9047,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 23.4953,
        },
        {
          sector: "Wholesale Trade",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.5467,
        },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 0.5467,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 45.998 },
        {
          sector: "Retail Trade",
          code: "441",
          subsector: "Motor Vehicle and Parts Dealers",
          weightPct: 4.456,
        },
        {
          sector: "Retail Trade",
          code: "442",
          subsector: "Furniture & Home Furnishings Stores",
          weightPct: 0.5687,
        },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 8.4059,
        },
        {
          sector: "Retail Trade",
          code: "448",
          subsector: "Clothing & Clothing Accessories Stores",
          weightPct: 5.8999,
        },
        {
          sector: "Retail Trade",
          code: "453",
          subsector: "Miscellaneous Store Retailers",
          weightPct: 0.483,
        },
        {
          sector: "Retail Trade",
          code: "454",
          subsector: "Nonstore Retailers",
          weightPct: 26.1844,
        },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 6.125,
        },
        {
          sector: "Transportation & Warehousing",
          code: "483",
          subsector: "Water Transportation",
          weightPct: 2.4266,
        },
        {
          sector: "Transportation & Warehousing",
          code: "488",
          subsector: "Support Activities for Transportation",
          weightPct: 3.6984,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 3.8335,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 3.8335,
        },
        {
          sector: "Accommodation & Food Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 13.3704,
        },
        {
          sector: "Accommodation & Food Services",
          code: "721",
          subsector: "Accommodation",
          weightPct: 4.6447,
        },
        {
          sector: "Accommodation & Food Services",
          code: "722",
          subsector: "Food Services and Drinking Places",
          weightPct: 8.7258,
        },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.5293 },
      ],
    },
    XLP: {
      underlyingTicker: "XLP",
      asOf: "2026-06-08",
      entries: [
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 66.5224 },
        {
          sector: "Manufacturing",
          code: "311",
          subsector: "Food Manufacturing",
          weightPct: 31.9756,
        },
        {
          sector: "Manufacturing",
          code: "312",
          subsector: "Beverage and Tobacco Product Manufacturing",
          weightPct: 14.9298,
        },
        {
          sector: "Manufacturing",
          code: "322",
          subsector: "Paper Manufacturing",
          weightPct: 2.1563,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 17.4607,
        },
        {
          sector: "Wholesale Trade",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 2.4343,
        },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 2.4343,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 30.4929 },
        {
          sector: "Retail Trade",
          code: "441",
          subsector: "Motor Vehicle and Parts Dealers",
          weightPct: 0.9349,
        },
        {
          sector: "Retail Trade",
          code: "445",
          subsector: "Food and Beverage Retailers",
          weightPct: 2.4362,
        },
        {
          sector: "Retail Trade",
          code: "452",
          subsector: "General Merchandise Stores",
          weightPct: 27.1218,
        },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.5504 },
      ],
    },
    XLE: {
      underlyingTicker: "XLE",
      asOf: "2026-06-08",
      entries: [
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 21.9506,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "211",
          subsector: "Oil and Gas Extraction",
          weightPct: 15.3529,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "213",
          subsector: "Support Activities for Mining",
          weightPct: 6.5976,
        },
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 3.0887 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 3.0887 },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 62.307 },
        {
          sector: "Manufacturing",
          code: "324",
          subsector: "Petroleum and Coal Products Manufacturing",
          weightPct: 58.7389,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 3.5681,
        },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 10.7729,
        },
        {
          sector: "Transportation & Warehousing",
          code: "486",
          subsector: "Pipeline Transportation",
          weightPct: 10.7729,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.2773,
        },
        {
          sector: "Finance & Insurance",
          code: "523",
          subsector:
            "Securities, Commodity Contracts, and Other Financial Investments and Related Activities",
          weightPct: 1.2773,
        },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.6036 },
      ],
    },
    XLF: {
      underlyingTicker: "XLF",
      asOf: "2026-06-08",
      entries: [
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 0.517 },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 0.517,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 81.2495,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 35.3578,
        },
        {
          sector: "Finance & Insurance",
          code: "523",
          subsector:
            "Securities, Commodity Contracts, and Other Financial Investments and Related Activities",
          weightPct: 20.8008,
        },
        {
          sector: "Finance & Insurance",
          code: "524",
          subsector: "Insurance Carriers and Related Activities",
          weightPct: 25.0909,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 17.544,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 17.544,
        },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 0.1393 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.5502 },
      ],
    },
    VUG: {
      underlyingTicker: "VUG",
      asOf: "2026-04-30",
      entries: [
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.2693,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "211",
          subsector: "Oil and Gas Extraction",
          weightPct: 0.069,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "212",
          subsector: "Mining (except Oil and Gas)",
          weightPct: 0.2002,
        },
        { sector: "Construction", code: null, subsector: "(sector subtotal)", weightPct: 0.4512 },
        {
          sector: "Construction",
          code: "238",
          subsector: "Specialty Trade Contractors",
          weightPct: 0.4512,
        },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 63.3757 },
        {
          sector: "Manufacturing",
          code: "312",
          subsector: "Beverage and Tobacco Product Manufacturing",
          weightPct: 0.168,
        },
        {
          sector: "Manufacturing",
          code: "315",
          subsector: "Apparel Manufacturing",
          weightPct: 0.2397,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 3.2556,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 0.2967,
        },
        {
          sector: "Manufacturing",
          code: "332",
          subsector: "Fabricated Metal Product Manufacturing",
          weightPct: 0.0994,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 1.7424,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 50.8,
        },
        {
          sector: "Manufacturing",
          code: "335",
          subsector: "Electrical Equipment, Appliance, and Component Manufacturing",
          weightPct: 1.4387,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 4.0006,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 1.3344,
        },
        {
          sector: "Wholesale Trade",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.1671,
        },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 0.1671,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 8.035 },
        {
          sector: "Retail Trade",
          code: "441",
          subsector: "Motor Vehicle and Parts Dealers",
          weightPct: 0.6805,
        },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 0.3979,
        },
        {
          sector: "Retail Trade",
          code: "448",
          subsector: "Clothing & Clothing Accessories Stores",
          weightPct: 0.5186,
        },
        {
          sector: "Retail Trade",
          code: "452",
          subsector: "General Merchandise Stores",
          weightPct: 1.3184,
        },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 5.1196 },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.8095,
        },
        {
          sector: "Transportation & Warehousing",
          code: "484",
          subsector: "Truck Transportation",
          weightPct: 0.132,
        },
        {
          sector: "Transportation & Warehousing",
          code: "486",
          subsector: "Pipeline Transportation",
          weightPct: 0.1785,
        },
        {
          sector: "Transportation & Warehousing",
          code: "488",
          subsector: "Support Activities for Transportation",
          weightPct: 0.4991,
        },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 0.3852 },
        { sector: "Information", code: "517", subsector: "Telecommunications", weightPct: 0.1381 },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 0.2471,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.9488,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 0.1296,
        },
        {
          sector: "Finance & Insurance",
          code: "523",
          subsector:
            "Securities, Commodity Contracts, and Other Financial Investments and Related Activities",
          weightPct: 0.8516,
        },
        {
          sector: "Finance & Insurance",
          code: "525",
          subsector: "Funds, Trusts, and Other Financial Vehicles",
          weightPct: 0.9675,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.2197,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 1.2197,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 15.9593,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 15.9593,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 4.3113,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 4.3113,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.0905,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "711",
          subsector: "Performing Arts, Spectator Sports, and Related Industries",
          weightPct: 0.0905,
        },
        {
          sector: "Accommodation & Food Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.7999,
        },
        {
          sector: "Accommodation & Food Services",
          code: "721",
          subsector: "Accommodation",
          weightPct: 0.5337,
        },
        {
          sector: "Accommodation & Food Services",
          code: "722",
          subsector: "Food Services and Drinking Places",
          weightPct: 1.2661,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 0.6982 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4792 },
      ],
    },
    XLV: {
      underlyingTicker: "XLV",
      asOf: "2026-06-08",
      entries: [
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 79.6819 },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 58.8362,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 10.1109,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 10.7348,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 5.7794 },
        {
          sector: "Retail Trade",
          code: "446",
          subsector: "Health & Personal Care Stores",
          weightPct: 5.7794,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 11.0603,
        },
        {
          sector: "Finance & Insurance",
          code: "524",
          subsector: "Insurance Carriers and Related Activities",
          weightPct: 11.0603,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.036,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 1.036,
        },
        {
          sector: "Health Care & Social Assistance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.9552,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "621",
          subsector: "Ambulatory Health Care Services",
          weightPct: 0.7869,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "622",
          subsector: "Hospitals",
          weightPct: 1.1682,
        },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4873 },
      ],
    },
    XLI: {
      underlyingTicker: "XLI",
      asOf: "2026-06-08",
      entries: [
        { sector: "Construction", code: null, subsector: "(sector subtotal)", weightPct: 4.0589 },
        {
          sector: "Construction",
          code: "237",
          subsector: "Heavy and Civil Engineering Construction",
          weightPct: 0.2599,
        },
        {
          sector: "Construction",
          code: "238",
          subsector: "Specialty Trade Contractors",
          weightPct: 3.799,
        },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 69.5594 },
        {
          sector: "Manufacturing",
          code: "315",
          subsector: "Apparel Manufacturing",
          weightPct: 1.0902,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 1.8272,
        },
        {
          sector: "Manufacturing",
          code: "332",
          subsector: "Fabricated Metal Product Manufacturing",
          weightPct: 6.2678,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 19.7411,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 9.4978,
        },
        {
          sector: "Manufacturing",
          code: "335",
          subsector: "Electrical Equipment, Appliance, and Component Manufacturing",
          weightPct: 13.1269,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 16.5103,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 1.4981,
        },
        { sector: "Wholesale Trade", code: null, subsector: "(sector subtotal)", weightPct: 1.055 },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 1.055,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 1.4833 },
        {
          sector: "Retail Trade",
          code: "441",
          subsector: "Motor Vehicle and Parts Dealers",
          weightPct: 0.5077,
        },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 0.9756,
        },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 12.9987,
        },
        {
          sector: "Transportation & Warehousing",
          code: "481",
          subsector: "Air Transportation",
          weightPct: 1.9406,
        },
        {
          sector: "Transportation & Warehousing",
          code: "482",
          subsector: "Rail Transportation",
          weightPct: 5.8523,
        },
        {
          sector: "Transportation & Warehousing",
          code: "484",
          subsector: "Truck Transportation",
          weightPct: 2.7096,
        },
        {
          sector: "Transportation & Warehousing",
          code: "488",
          subsector: "Support Activities for Transportation",
          weightPct: 0.8182,
        },
        {
          sector: "Transportation & Warehousing",
          code: "492",
          subsector: "Couriers and Messengers",
          weightPct: 1.678,
        },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 2.1665 },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 2.1665,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.2614,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 1.2614,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.88,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 0.88,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 6.0593,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 3.8149,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "562",
          subsector: "Waste Management and Remediation Services",
          weightPct: 2.2444,
        },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4774 },
      ],
    },
    PAVE: {
      underlyingTicker: "PAVE",
      asOf: "2026-06-08",
      entries: [
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 5.1275,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "212",
          subsector: "Mining (except Oil and Gas)",
          weightPct: 5.1275,
        },
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 3.1332 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 3.1332 },
        { sector: "Construction", code: null, subsector: "(sector subtotal)", weightPct: 14.6686 },
        {
          sector: "Construction",
          code: "236",
          subsector: "Construction of Buildings",
          weightPct: 0.713,
        },
        {
          sector: "Construction",
          code: "237",
          subsector: "Heavy and Civil Engineering Construction",
          weightPct: 6.3516,
        },
        {
          sector: "Construction",
          code: "238",
          subsector: "Specialty Trade Contractors",
          weightPct: 7.604,
        },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 55.754 },
        {
          sector: "Manufacturing",
          code: "321",
          subsector: "Wood Product Manufacturing",
          weightPct: 0.2634,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 1.8576,
        },
        {
          sector: "Manufacturing",
          code: "326",
          subsector: "Plastics and Rubber Products Manufacturing",
          weightPct: 1.3108,
        },
        {
          sector: "Manufacturing",
          code: "327",
          subsector: "Nonmetallic Mineral Product Manufacturing",
          weightPct: 4.1121,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 14.8065,
        },
        {
          sector: "Manufacturing",
          code: "332",
          subsector: "Fabricated Metal Product Manufacturing",
          weightPct: 9.0544,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 9.5984,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 9.4736,
        },
        {
          sector: "Manufacturing",
          code: "335",
          subsector: "Electrical Equipment, Appliance, and Component Manufacturing",
          weightPct: 5.0714,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 0.2057,
        },
        {
          sector: "Wholesale Trade",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 2.6317,
        },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 2.6317,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 3.642 },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 3.2502,
        },
        {
          sector: "Retail Trade",
          code: "453",
          subsector: "Miscellaneous Store Retailers",
          weightPct: 0.3918,
        },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 9.7315,
        },
        {
          sector: "Transportation & Warehousing",
          code: "482",
          subsector: "Rail Transportation",
          weightPct: 9.7315,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 3.7966,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 3.7966,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.1054,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 1.1054,
        },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4095 },
      ],
    },
    XLB: {
      underlyingTicker: "XLB",
      asOf: "2026-06-08",
      entries: [
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 4.3173,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "111",
          subsector: "Crop Production",
          weightPct: 4.3173,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 21.1114,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "212",
          subsector: "Mining (except Oil and Gas)",
          weightPct: 21.1114,
        },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 69.9063 },
        {
          sector: "Manufacturing",
          code: "322",
          subsector: "Paper Manufacturing",
          weightPct: 8.7887,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 41.5724,
        },
        {
          sector: "Manufacturing",
          code: "327",
          subsector: "Nonmetallic Mineral Product Manufacturing",
          weightPct: 4.3687,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 11.209,
        },
        {
          sector: "Manufacturing",
          code: "332",
          subsector: "Fabricated Metal Product Manufacturing",
          weightPct: 1.7718,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 2.1957,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 4.3122 },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 4.3122,
        },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.3528 },
      ],
    },
    IWC: {
      underlyingTicker: "IWC",
      asOf: "2026-03-31",
      entries: [
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.245,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "111",
          subsector: "Crop Production",
          weightPct: 0.121,
        },
        {
          sector: "Agriculture, Forestry, Fishing & Hunting",
          code: "115",
          subsector: "Support Activities for Agriculture and Forestry",
          weightPct: 0.124,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 2.611,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "211",
          subsector: "Oil and Gas Extraction",
          weightPct: 0.699,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "212",
          subsector: "Mining (except Oil and Gas)",
          weightPct: 1.5332,
        },
        {
          sector: "Mining, Quarrying, Oil & Gas",
          code: "213",
          subsector: "Support Activities for Mining",
          weightPct: 0.3788,
        },
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 1.1216 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 1.1216 },
        { sector: "Construction", code: null, subsector: "(sector subtotal)", weightPct: 1.8532 },
        {
          sector: "Construction",
          code: "236",
          subsector: "Construction of Buildings",
          weightPct: 0.9666,
        },
        {
          sector: "Construction",
          code: "237",
          subsector: "Heavy and Civil Engineering Construction",
          weightPct: 0.2976,
        },
        {
          sector: "Construction",
          code: "238",
          subsector: "Specialty Trade Contractors",
          weightPct: 0.589,
        },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 37.9557 },
        {
          sector: "Manufacturing",
          code: "311",
          subsector: "Food Manufacturing",
          weightPct: 0.3698,
        },
        {
          sector: "Manufacturing",
          code: "312",
          subsector: "Beverage and Tobacco Product Manufacturing",
          weightPct: 0.3867,
        },
        { sector: "Manufacturing", code: "313", subsector: "Textile Mills", weightPct: 0.0405 },
        {
          sector: "Manufacturing",
          code: "314",
          subsector: "Textile Product Mills",
          weightPct: 0.3358,
        },
        {
          sector: "Manufacturing",
          code: "315",
          subsector: "Apparel Manufacturing",
          weightPct: 0.0775,
        },
        {
          sector: "Manufacturing",
          code: "316",
          subsector: "Leather and Allied Product Manufacturing",
          weightPct: 0.168,
        },
        {
          sector: "Manufacturing",
          code: "321",
          subsector: "Wood Product Manufacturing",
          weightPct: 0.2262,
        },
        {
          sector: "Manufacturing",
          code: "322",
          subsector: "Paper Manufacturing",
          weightPct: 0.345,
        },
        {
          sector: "Manufacturing",
          code: "323",
          subsector: "Printing and Related Support Activities",
          weightPct: 0.2955,
        },
        {
          sector: "Manufacturing",
          code: "325",
          subsector: "Chemical Manufacturing",
          weightPct: 13.0889,
        },
        {
          sector: "Manufacturing",
          code: "326",
          subsector: "Plastics and Rubber Products Manufacturing",
          weightPct: 0.319,
        },
        {
          sector: "Manufacturing",
          code: "327",
          subsector: "Nonmetallic Mineral Product Manufacturing",
          weightPct: 0.0258,
        },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 1.2496,
        },
        {
          sector: "Manufacturing",
          code: "332",
          subsector: "Fabricated Metal Product Manufacturing",
          weightPct: 0.6403,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 1.8939,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 10.7679,
        },
        {
          sector: "Manufacturing",
          code: "335",
          subsector: "Electrical Equipment, Appliance, and Component Manufacturing",
          weightPct: 1.4216,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 2.3616,
        },
        {
          sector: "Manufacturing",
          code: "337",
          subsector: "Furniture and Related Product Manufacturing",
          weightPct: 0.465,
        },
        {
          sector: "Manufacturing",
          code: "339",
          subsector: "Miscellaneous Manufacturing",
          weightPct: 3.477,
        },
        {
          sector: "Wholesale Trade",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.2553,
        },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 1.2553,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 2.3759 },
        {
          sector: "Retail Trade",
          code: "441",
          subsector: "Motor Vehicle and Parts Dealers",
          weightPct: 0.3013,
        },
        {
          sector: "Retail Trade",
          code: "444",
          subsector: "Building Material and Garden Equipment and Supplies Dealers",
          weightPct: 0.0143,
        },
        {
          sector: "Retail Trade",
          code: "445",
          subsector: "Food and Beverage Retailers",
          weightPct: 0.2364,
        },
        {
          sector: "Retail Trade",
          code: "446",
          subsector: "Health & Personal Care Stores",
          weightPct: 0.0145,
        },
        {
          sector: "Retail Trade",
          code: "448",
          subsector: "Clothing & Clothing Accessories Stores",
          weightPct: 0.383,
        },
        {
          sector: "Retail Trade",
          code: "451",
          subsector: "Sporting Goods, Hobby, Book & Music Stores",
          weightPct: 0.2621,
        },
        {
          sector: "Retail Trade",
          code: "453",
          subsector: "Miscellaneous Store Retailers",
          weightPct: 0.6069,
        },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 0.5575 },
        {
          sector: "Transportation & Warehousing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.9814,
        },
        {
          sector: "Transportation & Warehousing",
          code: "481",
          subsector: "Air Transportation",
          weightPct: 0.2727,
        },
        {
          sector: "Transportation & Warehousing",
          code: "482",
          subsector: "Rail Transportation",
          weightPct: 0.1667,
        },
        {
          sector: "Transportation & Warehousing",
          code: "483",
          subsector: "Water Transportation",
          weightPct: 0.0375,
        },
        {
          sector: "Transportation & Warehousing",
          code: "484",
          subsector: "Truck Transportation",
          weightPct: 0.1782,
        },
        {
          sector: "Transportation & Warehousing",
          code: "486",
          subsector: "Pipeline Transportation",
          weightPct: 0.0642,
        },
        {
          sector: "Transportation & Warehousing",
          code: "488",
          subsector: "Support Activities for Transportation",
          weightPct: 0.2544,
        },
        {
          sector: "Transportation & Warehousing",
          code: "492",
          subsector: "Couriers and Messengers",
          weightPct: 0.0076,
        },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 2.991 },
        {
          sector: "Information",
          code: "511",
          subsector: "Publishing Industries (except Internet)",
          weightPct: 0.2947,
        },
        {
          sector: "Information",
          code: "512",
          subsector: "Motion Picture and Sound Recording Industries",
          weightPct: 0.1813,
        },
        {
          sector: "Information",
          code: "515",
          subsector: "Broadcasting (except Internet)",
          weightPct: 0.2115,
        },
        { sector: "Information", code: "517", subsector: "Telecommunications", weightPct: 0.7014 },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 1.6021,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 23.0466,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 18.3807,
        },
        {
          sector: "Finance & Insurance",
          code: "523",
          subsector:
            "Securities, Commodity Contracts, and Other Financial Investments and Related Activities",
          weightPct: 0.1752,
        },
        {
          sector: "Finance & Insurance",
          code: "524",
          subsector: "Insurance Carriers and Related Activities",
          weightPct: 1.4992,
        },
        {
          sector: "Finance & Insurance",
          code: "525",
          subsector: "Funds, Trusts, and Other Financial Vehicles",
          weightPct: 2.9914,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.5273,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "531",
          subsector: "Real Estate",
          weightPct: 1.051,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 0.2066,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "533",
          subsector: "Lessors of Nonfinancial Intangible Assets (except Copyrighted Works)",
          weightPct: 0.2697,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 3.8587,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 3.8587,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 2.3745,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 2.1338,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "562",
          subsector: "Waste Management and Remediation Services",
          weightPct: 0.2407,
        },
        {
          sector: "Educational Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.3823,
        },
        {
          sector: "Educational Services",
          code: "611",
          subsector: "Educational Services",
          weightPct: 0.3823,
        },
        {
          sector: "Health Care & Social Assistance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.4542,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "621",
          subsector: "Ambulatory Health Care Services",
          weightPct: 1.4163,
        },
        {
          sector: "Health Care & Social Assistance",
          code: "623",
          subsector: "Nursing and Residential Care Facilities",
          weightPct: 0.0379,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.2157,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "711",
          subsector: "Performing Arts, Spectator Sports, and Related Industries",
          weightPct: 0.0082,
        },
        {
          sector: "Arts, Entertainment & Recreation",
          code: "713",
          subsector: "Amusement, Gambling, and Recreation Industries",
          weightPct: 0.2076,
        },
        {
          sector: "Accommodation & Food Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.8383,
        },
        {
          sector: "Accommodation & Food Services",
          code: "721",
          subsector: "Accommodation",
          weightPct: 0.0692,
        },
        {
          sector: "Accommodation & Food Services",
          code: "722",
          subsector: "Food Services and Drinking Places",
          weightPct: 0.7692,
        },
        { sector: "Other Services", code: null, subsector: "(sector subtotal)", weightPct: 0.2378 },
        {
          sector: "Other Services",
          code: "811",
          subsector: "Repair and Maintenance",
          weightPct: 0.0139,
        },
        {
          sector: "Other Services",
          code: "812",
          subsector: "Personal and Laundry Services",
          weightPct: 0.2239,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 5.5731 },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 8.5373 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.5643 },
      ],
    },
    XLRE: {
      underlyingTicker: "XLRE",
      asOf: "2026-06-08",
      entries: [
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 92.4443,
        },
        {
          sector: "Finance & Insurance",
          code: "525",
          subsector: "Funds, Trusts, and Other Financial Vehicles",
          weightPct: 92.4443,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 5.7899,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "531",
          subsector: "Real Estate",
          weightPct: 5.7899,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.4935,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 1.4935,
        },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.2722 },
      ],
    },
    KRE: {
      underlyingTicker: "KRE",
      asOf: "2026-06-08",
      entries: [
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 96.2559,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 96.2559,
        },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 3.1603 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.5839 },
      ],
    },
    SMH: {
      underlyingTicker: "SMH",
      asOf: "2026-06-08",
      entries: [
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 83.4793 },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 8.8296,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 74.6496,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 16.2792 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.2416 },
      ],
    },
    XLK: {
      underlyingTicker: "XLK",
      asOf: "2026-06-08",
      entries: [
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 96.4632 },
        {
          sector: "Manufacturing",
          code: "331",
          subsector: "Primary Metal Manufacturing",
          weightPct: 0.9426,
        },
        {
          sector: "Manufacturing",
          code: "333",
          subsector: "Machinery Manufacturing",
          weightPct: 4.8721,
        },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 90.6486,
        },
        {
          sector: "Wholesale Trade",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 0.3884,
        },
        {
          sector: "Wholesale Trade",
          code: "425",
          subsector: "Wholesale Trade Agents and Brokers",
          weightPct: 0.3884,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 0.113 },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 0.113 },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 0.1971 },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 0.1971,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.2971,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 1.2971,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.0067,
        },
        {
          sector: "Administrative & Support & Waste Mgmt",
          code: "561",
          subsector: "Administrative and Support Services",
          weightPct: 1.0067,
        },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.5345 },
      ],
    },
    XLU: {
      underlyingTicker: "XLU",
      asOf: "2026-06-08",
      entries: [
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 99.5716 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 99.5716 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 0.4284 },
      ],
    },
    "COP-U CN": { underlyingTicker: "COP-U CN", asOf: "", entries: [] },
    PDBC: { underlyingTicker: "PDBC", asOf: "", entries: [] },
    USO: { underlyingTicker: "USO", asOf: "", entries: [] },
    UNG: { underlyingTicker: "UNG", asOf: "", entries: [] },
    "U-U CN": { underlyingTicker: "U-U CN", asOf: "", entries: [] },
    PALL: { underlyingTicker: "PALL", asOf: "", entries: [] },
    AGIX: {
      underlyingTicker: "AGIX",
      asOf: "2026-06-09",
      entries: [
        { sector: "Utilities", code: null, subsector: "(sector subtotal)", weightPct: 1.2912 },
        { sector: "Utilities", code: "221", subsector: "Utilities", weightPct: 1.2912 },
        { sector: "Manufacturing", code: null, subsector: "(sector subtotal)", weightPct: 51.5476 },
        {
          sector: "Manufacturing",
          code: "334",
          subsector: "Computer and Electronic Product Manufacturing",
          weightPct: 49.3261,
        },
        {
          sector: "Manufacturing",
          code: "336",
          subsector: "Transportation Equipment Manufacturing",
          weightPct: 2.2214,
        },
        { sector: "Retail Trade", code: null, subsector: "(sector subtotal)", weightPct: 3.07 },
        { sector: "Retail Trade", code: "454", subsector: "Nonstore Retailers", weightPct: 3.07 },
        { sector: "Information", code: null, subsector: "(sector subtotal)", weightPct: 1.5097 },
        {
          sector: "Information",
          code: "518",
          subsector:
            "Computing Infrastructure Providers, Data Processing, Web Hosting, and Related Services",
          weightPct: 1.5097,
        },
        {
          sector: "Finance & Insurance",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 5.3741,
        },
        {
          sector: "Finance & Insurance",
          code: "522",
          subsector: "Credit Intermediation and Related Activities",
          weightPct: 5.3741,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 1.3621,
        },
        {
          sector: "Real Estate & Rental & Leasing",
          code: "532",
          subsector: "Rental and Leasing Services",
          weightPct: 1.3621,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: null,
          subsector: "(sector subtotal)",
          weightPct: 10.0713,
        },
        {
          sector: "Professional, Scientific & Technical Services",
          code: "541",
          subsector: "Professional, Scientific, and Technical Services",
          weightPct: 10.0713,
        },
        { sector: "", code: null, subsector: "Non-US holdings (no NAICS)", weightPct: 21.9741 },
        { sector: "", code: null, subsector: "US holdings not classified", weightPct: 1.785 },
        { sector: "", code: null, subsector: "Cash & long-tail", weightPct: 2.0149 },
      ],
    },
  });

/**
 * Build the `Sector[]` payload that `<FundSectors>` expects, from a
 * leveraged-2x fund's underlying ETF ticker.
 *
 * The xlsx provides a full NAICS breakdown of the underlying ETF — i.e. the
 * sectors of the *index* the 2x fund swaps to. This is the canonical source
 * for the lev-2x Sector Breakdown card (replacing top-10-derived sectors,
 * which were necessarily incomplete because only the top 10 holdings flow
 * through the live API).
 *
 * Sums the per-sector subtotal rows (code === null, sector !== "") and rolls
 * unclassified rows (Non-US holdings, US-not-classified, Cash & long-tail)
 * into a single "Other" bucket. Each output entry gets a palette color from
 * `getSectorColor` (NAICS macro-sectors that aren't in the palette fall back
 * to the catch-all `#6b6259`).
 *
 * Returns `null` when the underlying isn't covered by the xlsx (e.g. FDRS,
 * EUVX, RCKZ — Corgi-internal indices) OR the xlsx marks it as N/A
 * (physical-commodity / futures trusts: IAU, PLTM, SIVR, COP-U CN, PDBC,
 * USO, UNG, U-U CN, PALL). Per project rule (no-fake-fallback-data), the
 * caller should render the empty state in either case rather than inventing
 * placeholder rows.
 */
export function getLeveragedSectorBreakdown(
  underlyingTicker: string | undefined | null,
): { sectors: Sector[]; asOf: string } | null {
  if (!underlyingTicker) return null;
  const payload = SECTOR_BREAKDOWN_BY_UNDERLYING[underlyingTicker];
  if (!payload || payload.entries.length === 0) return null;

  // Subtotal rows = per-NAICS-sector aggregate. The xlsx already sums the
  // 3-digit subsectors into the subtotal row above them, so we don't need
  // to re-aggregate.
  const subtotals = payload.entries.filter((e) => e.code === null && e.sector !== "");
  // Unclassified rows (Non-US, US-not-classified, Cash & long-tail) →
  // collapse into a single "Other" segment so the dot legend stays compact.
  // For non-US-heavy underlyings (e.g. KWEB, VWO), "Other" dominates the
  // breakdown — the user-facing tooltip ("X% Non-US / unclassified") makes
  // that explicit rather than burying it under the bar.
  const unclassified = payload.entries.filter((e) => e.code === null && e.sector === "");
  const otherPct = unclassified.reduce((s, e) => s + e.weightPct, 0);

  // Build Sector[] — per-sector subsector count surfaces in the legend
  // subtitle so the breakdown reads as "sectors -> subsectors", matching
  // the xlsx's two-level structure. Companies count = distinct 3-digit
  // NAICS subsectors in that sector (most have 1-5).
  const sectors: Sector[] = subtotals.map((sub) => {
    const subsectorCount = payload.entries.filter(
      (e) => e.code !== null && e.sector === sub.sector,
    ).length;
    return {
      name: sub.sector,
      pct: Math.round(sub.weightPct * 100) / 100,
      companies: Math.max(1, subsectorCount),
      color: getSectorColor(sub.sector),
      description:
        subsectorCount > 0
          ? `${subsectorCount} subsector${subsectorCount === 1 ? "" : "s"}`
          : undefined,
    };
  });

  if (otherPct > 0.005) {
    sectors.push({
      name: "Other",
      pct: Math.round(otherPct * 100) / 100,
      companies: Math.max(1, unclassified.length),
      color: getSectorColor("Other"),
      description: "Non-US / unclassified",
    });
  }

  sectors.sort((a, b) => b.pct - a.pct);
  return { sectors, asOf: payload.asOf };
}
